/*
 * <tss-tab-nav> — a tab bar that filters [data-tab] elements anywhere on the
 * page, not just inside itself.
 *
 * Sections cap at 50 blocks, so a long tabbed page splits its rows across
 * several sections. Scoping the query to the document (rather than the tab
 * bar's own subtree) is what lets one bar drive all of them.
 */
const TAB_PARAM = 'tab';

class TssTabNav extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-tab-target]')];
    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.tabTarget, { updateUrl: true }));
      tab.addEventListener('keydown', (event) => this.onKeydown(event));
    });

    this.select(this.fromUrl() || this.tabs[0].dataset.tabTarget, { scroll: false });

    this.popstateListener = () => this.select(this.fromUrl() || this.tabs[0].dataset.tabTarget, { scroll: false });
    window.addEventListener('popstate', this.popstateListener);

    // Panels can arrive later when a section is re-rendered in the theme editor.
    document.addEventListener('shopify:section:load', () => this.apply());
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.popstateListener);
  }

  fromUrl() {
    const value = new URL(window.location.href).searchParams.get(TAB_PARAM);
    return this.tabs.some((tab) => tab.dataset.tabTarget === value) ? value : null;
  }

  select(index, { updateUrl = false, focusTab = false, scroll = true } = {}) {
    const active = this.tabs.find((tab) => tab.dataset.tabTarget === String(index));
    if (!active) return;

    this.active = String(index);
    this.tabs.forEach((tab) => tab.setAttribute('aria-selected', String(tab === active)));
    this.apply();

    if (scroll) active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    if (focusTab) active.focus();
    if (updateUrl) this.writeUrl(this.active);
  }

  /* "all" panels stay put; everything else shows only for its own tab. */
  apply() {
    if (!this.active) return;
    document.querySelectorAll('[data-tab]').forEach((panel) => {
      const value = panel.dataset.tab;
      panel.hidden = value !== 'all' && value !== this.active;
    });
  }

  writeUrl(index) {
    const url = new URL(window.location.href);
    if (url.searchParams.get(TAB_PARAM) === index) return;
    url.searchParams.set(TAB_PARAM, index);
    history.replaceState(history.state, '', url);
  }

  onKeydown(event) {
    const steps = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const step = steps[event.key];
    if (!step) return;
    event.preventDefault();
    const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const next = (current + step + this.tabs.length) % this.tabs.length;
    this.select(this.tabs[next].dataset.tabTarget, { updateUrl: true, focusTab: true });
  }
}

if (!customElements.get('tss-tab-nav')) customElements.define('tss-tab-nav', TssTabNav);
